import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Popconfirm,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  useKycQuestionnaireForm,
  type QuestionFormItem,
} from "./useKycQuestionnaireForm";
import { useKycQuestionnaireDetail } from "./useKycQuestionnaireDetail";
import { KycQuestionType } from "../../graphql";

const questionTypes = [
  { value: "SINGLE_CHOICE", label: "Нэг сонголтот" },
  { value: "MULTIPLE_CHOICE", label: "Олон сонголтот" },
  { value: "FREE_TEXT", label: "Чөлөөт текст" },
  { value: "NUMBER", label: "Тоо" },
  { value: "DATE", label: "Огноо" },
  { value: "FILE_UPLOAD", label: "Файл хавсаргах" },
];

export const KycQuestionnaireFormWidget = ({ _id }: { _id: string }) => {
  const isCreate = _id === "create";
  const { data: questionnaire } = useKycQuestionnaireDetail({
    _id: isCreate ? "" : _id,
  });

  const {
    form,
    loading,
    questions,
    setQuestions,
    addKycQuestionnaire,
    editKycQuestionnaire,
  } = useKycQuestionnaireForm(
    isCreate ? { selectedId: undefined } : { selectedId: _id },
  );

  useEffect(() => {
    if (!isCreate && questionnaire) {
      form.setFields([
        {
          name: "name_mn",
          value: questionnaire.localizedName?.mn ?? questionnaire.name ?? "",
        },
        { name: "name_en", value: questionnaire.localizedName?.en ?? "" },
        {
          name: "description_mn",
          value:
            questionnaire.localizedDescription?.mn ??
            questionnaire.description ??
            "",
        },
        {
          name: "description_en",
          value: questionnaire.localizedDescription?.en ?? "",
        },
        { name: "active", value: questionnaire.active },
      ]);
      setQuestions(
        questionnaire.questions.map((q) => ({
          type: q.type as KycQuestionType,
          question_mn: q.localizedQuestion?.mn ?? q.question,
          question_en: q.localizedQuestion?.en ?? "",
          required: q.required,
          options:
            q.options?.map((o) => ({
              value_mn: o.localizedValue?.mn ?? o.value,
              value_en: o.localizedValue?.en ?? "",
              requiresInput: o.requiresInput ?? false,
              triggersSubQuestions: o.triggersSubQuestions ?? false,
            })) ?? [],
          subQuestions:
            q.subQuestions?.map((sq) => ({
              type: sq.type as KycQuestionType,
              question_mn: sq.localizedQuestion?.mn ?? sq.question,
              question_en: sq.localizedQuestion?.en ?? "",
              required: sq.required,
              options:
                sq.options?.map((o) => ({
                  value_mn: o.localizedValue?.mn ?? o.value,
                  value_en: o.localizedValue?.en ?? "",
                  requiresInput: o.requiresInput ?? false,
                  triggersSubQuestions: o.triggersSubQuestions ?? false,
                })) ?? [],
              order: sq.order,
            })) ?? [],
          order: q.order,
        })),
      );
    }
  }, [isCreate, questionnaire, form, setQuestions]);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        type: KycQuestionType.FreeText,
        question_mn: "",
        question_en: "",
        required: true,
        options: [],
        subQuestions: [],
        order: questions.length,
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  };

  const updateQuestion = (
    index: number,
    field: keyof QuestionFormItem,
    value: unknown,
  ) => {
    const updated = [...questions];
    (updated[index] as Record<string, unknown>)[field] = value;
    setQuestions(updated);
  };

  const addOption = (qIndex: number) => {
    const updated = [...questions];
    updated[qIndex].options.push({
      value_mn: "",
      value_en: "",
      requiresInput: false,
      triggersSubQuestions: false,
    });
    setQuestions(updated);
  };

  const addSubQuestion = (qIndex: number) => {
    const updated = [...questions];
    updated[qIndex].subQuestions.push({
      type: KycQuestionType.FreeText,
      question_mn: "",
      question_en: "",
      required: true,
      options: [],
      order: updated[qIndex].subQuestions.length,
    });
    setQuestions(updated);
  };

  const removeSubQuestion = (qIndex: number, sqIndex: number) => {
    const updated = [...questions];
    updated[qIndex].subQuestions.splice(sqIndex, 1);
    setQuestions(updated);
  };

  const updateSubQuestion = <
    K extends keyof QuestionFormItem["subQuestions"][number],
  >(
    qIndex: number,
    sqIndex: number,
    field: K,
    value: QuestionFormItem["subQuestions"][number][K],
  ) => {
    const updated = [...questions];
    updated[qIndex].subQuestions[sqIndex][field] = value;
    setQuestions(updated);
  };

  const addSubOption = (qIndex: number, sqIndex: number) => {
    const updated = [...questions];
    updated[qIndex].subQuestions[sqIndex].options.push({
      value_mn: "",
      value_en: "",
      requiresInput: false,
      triggersSubQuestions: false,
    });
    setQuestions(updated);
  };

  const removeSubOption = (
    qIndex: number,
    sqIndex: number,
    oIndex: number,
  ) => {
    const updated = [...questions];
    updated[qIndex].subQuestions[sqIndex].options.splice(oIndex, 1);
    setQuestions(updated);
  };

  const updateSubOption = <
    K extends keyof QuestionFormItem["subQuestions"][number]["options"][number],
  >(
    qIndex: number,
    sqIndex: number,
    oIndex: number,
    field: K,
    value: QuestionFormItem["subQuestions"][number]["options"][number][K],
  ) => {
    const updated = [...questions];
    updated[qIndex].subQuestions[sqIndex].options[oIndex][field] = value;
    setQuestions(updated);
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    const updated = [...questions];
    updated[qIndex].options.splice(oIndex, 1);
    setQuestions(updated);
  };

  const updateOption = <
    K extends keyof QuestionFormItem["options"][number],
  >(
    qIndex: number,
    oIndex: number,
    field: K,
    value: QuestionFormItem["options"][number][K],
  ) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex][field] = value;
    setQuestions(updated);
  };

  return (
    <>
      <div className="page-header">
        <h3>{isCreate ? "KYC Судалгаа нэмэх" : "KYC Судалгаа засах"}</h3>
        <Space>
          <Link to="/kyc-questionnaires">
            <Button type="dashed" icon={<ArrowLeftOutlined />}>
              Буцах
            </Button>
          </Link>
        </Space>
      </div>
      <Form
        form={form}
        onFinish={isCreate ? addKycQuestionnaire : editKycQuestionnaire}
        style={{ marginTop: 20 }}
        layout="vertical"
      >
        <Form.Item
          label="Нэр (Монгол)"
          name="name_mn"
          rules={[{ required: true, message: "Монгол нэрийг бөглөнө үү" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Нэр (Англи)"
          name="name_en"
          rules={[{ required: true, message: "Англи нэрийг бөглөнө үү" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Тайлбар (Монгол)"
          name="description_mn"
          rules={[{ required: true, message: "Монгол тайлбарыг бөглөнө үү" }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item
          label="Тайлбар (Англи)"
          name="description_en"
          rules={[{ required: true, message: "Англи тайлбарыг бөглөнө үү" }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item label="Идэвхитэй эсэх" name="active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <h4>Асуултууд</h4>
        {questions.map((question, qIndex) => (
          <Card
            key={qIndex}
            size="small"
            style={{ marginBottom: 16 }}
            title={`Асуулт ${qIndex + 1}`}
            extra={
              <Popconfirm
                title="Устгах уу?"
                okText="Тийм"
                cancelText="Үгүй"
                onConfirm={() => removeQuestion(qIndex)}
              >
                <Button
                  size="small"
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            }
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Select
                value={question.type}
                onChange={(val) => updateQuestion(qIndex, "type", val)}
                options={questionTypes}
                style={{ width: 200 }}
              />
              <Input
                placeholder="Асуулт (Монгол)"
                value={question.question_mn}
                onChange={(e) =>
                  updateQuestion(qIndex, "question_mn", e.target.value)
                }
              />
              <Input
                placeholder="Асуулт (Англи)"
                value={question.question_en}
                onChange={(e) =>
                  updateQuestion(qIndex, "question_en", e.target.value)
                }
              />
              <Space>
                <span>Заавал:</span>
                <Switch
                  checked={question.required}
                  onChange={(val) => updateQuestion(qIndex, "required", val)}
                />
              </Space>

              {(question.type === "SINGLE_CHOICE" ||
                question.type === "MULTIPLE_CHOICE") && (
                <>
                  <h5>Сонголтууд</h5>
                  {question.options.map((option, oIndex) => (
                    <Space key={oIndex} wrap>
                      <Input
                        placeholder="Сонголт (МН)"
                        value={option.value_mn}
                        onChange={(e) =>
                          updateOption(
                            qIndex,
                            oIndex,
                            "value_mn",
                            e.target.value,
                          )
                        }
                      />
                      <Input
                        placeholder="Сонголт (EN)"
                        value={option.value_en}
                        onChange={(e) =>
                          updateOption(
                            qIndex,
                            oIndex,
                            "value_en",
                            e.target.value,
                          )
                        }
                      />
                      <Space size={4}>
                        <span style={{ fontSize: 12, color: "#666" }}>
                          Нэмэлт оруулга:
                        </span>
                        <Switch
                          size="small"
                          checked={option.requiresInput}
                          onChange={(val) =>
                            updateOption(qIndex, oIndex, "requiresInput", val)
                          }
                        />
                      </Space>
                      <Space size={4}>
                        <span style={{ fontSize: 12, color: "#666" }}>
                          Нэмэлт асуулт:
                        </span>
                        <Switch
                          size="small"
                          checked={option.triggersSubQuestions}
                          onChange={(val) =>
                            updateOption(
                              qIndex,
                              oIndex,
                              "triggersSubQuestions",
                              val,
                            )
                          }
                        />
                      </Space>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeOption(qIndex, oIndex)}
                      />
                    </Space>
                  ))}
                  <Button
                    size="small"
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => addOption(qIndex)}
                  >
                    Сонголт нэмэх
                  </Button>
                </>
              )}

              {(question.type === "SINGLE_CHOICE" ||
                question.type === "MULTIPLE_CHOICE") &&
                question.options.some((o) => o.triggersSubQuestions) && (
                  <>
                    <h5>Нэмэлт асуултууд (заасан сонголт сонгогдвол гарч ирнэ)</h5>
                    {question.subQuestions.map((sq, sqIndex) => (
                      <Card
                        key={sqIndex}
                        size="small"
                        type="inner"
                        style={{ marginBottom: 8, marginLeft: 16 }}
                        title={`Нэмэлт асуулт ${sqIndex + 1}`}
                        extra={
                          <Popconfirm
                            title="Устгах уу?"
                            okText="Тийм"
                            cancelText="Үгүй"
                            onConfirm={() =>
                              removeSubQuestion(qIndex, sqIndex)
                            }
                          >
                            <Button
                              size="small"
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                        }
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Select
                            value={sq.type}
                            onChange={(val) =>
                              updateSubQuestion(qIndex, sqIndex, "type", val)
                            }
                            options={questionTypes}
                            style={{ width: 200 }}
                          />
                          <Input
                            placeholder="Асуулт (Монгол)"
                            value={sq.question_mn}
                            onChange={(e) =>
                              updateSubQuestion(
                                qIndex,
                                sqIndex,
                                "question_mn",
                                e.target.value,
                              )
                            }
                          />
                          <Input
                            placeholder="Асуулт (Англи)"
                            value={sq.question_en}
                            onChange={(e) =>
                              updateSubQuestion(
                                qIndex,
                                sqIndex,
                                "question_en",
                                e.target.value,
                              )
                            }
                          />
                          <Space>
                            <span>Заавал:</span>
                            <Switch
                              checked={sq.required}
                              onChange={(val) =>
                                updateSubQuestion(
                                  qIndex,
                                  sqIndex,
                                  "required",
                                  val,
                                )
                              }
                            />
                          </Space>
                          {(sq.type === "SINGLE_CHOICE" ||
                            sq.type === "MULTIPLE_CHOICE") && (
                            <>
                              <h5>Сонголтууд</h5>
                              {sq.options.map((opt, oIndex) => (
                                <Space key={oIndex} wrap>
                                  <Input
                                    placeholder="Сонголт (МН)"
                                    value={opt.value_mn}
                                    onChange={(e) =>
                                      updateSubOption(
                                        qIndex,
                                        sqIndex,
                                        oIndex,
                                        "value_mn",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Input
                                    placeholder="Сонголт (EN)"
                                    value={opt.value_en}
                                    onChange={(e) =>
                                      updateSubOption(
                                        qIndex,
                                        sqIndex,
                                        oIndex,
                                        "value_en",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Space size={4}>
                                    <span
                                      style={{ fontSize: 12, color: "#666" }}
                                    >
                                      Нэмэлт оруулга:
                                    </span>
                                    <Switch
                                      size="small"
                                      checked={opt.requiresInput}
                                      onChange={(val) =>
                                        updateSubOption(
                                          qIndex,
                                          sqIndex,
                                          oIndex,
                                          "requiresInput",
                                          val,
                                        )
                                      }
                                    />
                                  </Space>
                                  <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                      removeSubOption(qIndex, sqIndex, oIndex)
                                    }
                                  />
                                </Space>
                              ))}
                              <Button
                                size="small"
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => addSubOption(qIndex, sqIndex)}
                              >
                                Сонголт нэмэх
                              </Button>
                            </>
                          )}
                        </Space>
                      </Card>
                    ))}
                    <Button
                      size="small"
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => addSubQuestion(qIndex)}
                      style={{ marginLeft: 16 }}
                    >
                      Нэмэлт асуулт нэмэх
                    </Button>
                  </>
                )}
            </Space>
          </Card>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addQuestion}
          style={{ marginBottom: 16 }}
        >
          Асуулт нэмэх
        </Button>

        <br />
        <Button htmlType="submit" type="primary" loading={loading}>
          {isCreate ? "Нэмэх" : "Засах"}
        </Button>
      </Form>
    </>
  );
};
